import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { SectionId as VehicleSessionId } from "../features/AC/Vehicle/enums";
import { SectionId as WelcomeSectionId } from "../features/Welcome/enums";
import { setIsClient } from "../features/Welcome/welcome.slice";
import { RootState } from "../store";

import { FormType } from "./enums";

type FormState = {
  footerVisibility: boolean;
  isErrorVisible: boolean;
  errorMessage: string;
  type: FormType;
  visibleSections: { [key: string]: boolean };
};

const initialState: FormState = {
  footerVisibility: true,
  isErrorVisible: false,
  errorMessage: "",
  type: FormType.WELCOME,
  visibleSections: {},
};

const formSlice = createSlice({
  name: "formSettings",
  initialState,
  reducers: {
    setErrorVisibility(state, action: PayloadAction<boolean>) {
      state.isErrorVisible = action.payload;
    },
    setFooterVisibility(state, action: PayloadAction<boolean>) {
      state.footerVisibility = action.payload;
    },
    setFormType(state, action: PayloadAction<FormType>) {
      state.type = action.payload;
    },
    setErrorMessage(state, action: PayloadAction<string>) {
      state.errorMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setIsClient, (state, action: PayloadAction<string>) => {
      state.visibleSections[VehicleSessionId.AC_VEHICLE_DRIVEABLE] =
        action.payload === "true";
      state.visibleSections[WelcomeSectionId.OC_USER_INJURED] =
        action.payload === "false";
      state.visibleSections[WelcomeSectionId.OC_TYPE] =
        action.payload === "false";
    });
  },
});

export const {
  setErrorVisibility,
  setFooterVisibility,
  setFormType,
  setErrorMessage,
} = formSlice.actions;

export const selectFooterVisibility = (
  state: RootState
): FormState["footerVisibility"] => state.formSettings.footerVisibility;

export const isSectionVisible = (state: RootState, type: string): boolean =>
  state.formSettings?.visibleSections[type] || false;

export const selectCurrentRoute = (state: RootState): string =>
  state.router.location.pathname;

export const selectPrevRoute = (state: RootState): string =>
  state.router.location.prevPathname;

export const selectRoutes = createSelector(
  selectCurrentRoute,
  selectPrevRoute,
  (currentRoute, prevRoute) => ({ currentRoute, prevRoute })
);

export const selectFormType = (state: RootState): FormType =>
  state.formSettings.type;

export const selectShouldDisplayError = (state: RootState): boolean =>
  state.formSettings.isErrorVisible;

export const selectErrorMessage = (state: RootState): string =>
  state.formSettings.errorMessage;

export const { reducer } = formSlice;
